import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Minus, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { takeFilterAnchor } from './filterAnchor';
import { filterOptionsBySearch, shouldShowFilterSearch } from './filterModalSearch';

let measureCtx = null;

/**
 * Pixel width the anchored panel needs to show the longest label untruncated.
 * Row text is 13px; adds panel padding (12), row padding (20), a scrollbar
 * allowance (16) and each entry's own extras (checkbox, group counter, ...).
 */
const measureLongestLabel = (entries) => {
  if (typeof document === 'undefined' || entries.length === 0) return 0;
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  if (!measureCtx) return 0;
  const family = window.getComputedStyle(document.body).fontFamily || 'sans-serif';
  let widest = 0;
  for (const { text, bold, extra = 0 } of entries) {
    measureCtx.font = `${bold ? 500 : 400} 13px ${family}`;
    widest = Math.max(widest, measureCtx.measureText(text).width + extra);
  }
  return Math.ceil(widest + 12 + 20 + 16);
};

/**
 * Select-all / clear-all toolbar — only for multi-select when handlers are provided.
 */
const FilterModalMultiSelectToolbar = ({ onSelectAll, onClearAll, isDark, selectAllLabel, clearAllLabel, compact = false }) => {
  if (compact) {
    return (
      <div className="flex items-center justify-between px-1 pb-[4px] text-xs font-normal">
        <button
          type="button"
          onClick={onSelectAll}
          className="rounded-[6px] px-2 py-0.5 text-[#16A34A] hover:bg-[#E7F5EC] dark:hover:bg-[#17311F] transition-colors"
        >
          {selectAllLabel}
        </button>
        <button
          type="button"
          onClick={onClearAll}
          className="rounded-[6px] px-2 py-0.5 text-[#DC2626] dark:text-[#F87171] hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
        >
          {clearAllLabel}
        </button>
      </div>
    );
  }
  return (
    <div className="flex justify-center gap-4 px-5 py-3 border-b border-[#E8EBE9] dark:border-[#3a3c3b]">
      <button
        type="button"
        onClick={onSelectAll}
        className="px-6 py-2 bg-bloom-green hover:bg-bloom-green-hover text-white text-sm font-medium rounded-full transition-colors"
      >
        {selectAllLabel}
      </button>
      <button
        type="button"
        onClick={onClearAll}
        className={`px-6 py-2 text-sm font-medium rounded-full transition-colors ${
          isDark
            ? 'bg-gray-700 hover:bg-gray-600 text-white'
            : 'bg-[#F0F5F2] hover:bg-[#E8EBE9] text-[#282A29]'
        }`}
      >
        {clearAllLabel}
      </button>
    </div>
  );
};

/**
 * Primary action to confirm and close — shown for every filter modal (single- and multi-select).
 */
const FilterModalDoneFooter = ({ onClose, doneLabel }) => (
  <div className="px-5 py-3 border-t border-[#E8EBE9] dark:border-[#3a3c3b]">
    <button
      type="button"
      onClick={onClose}
      className="w-full py-2.5 bg-bloom-green hover:bg-bloom-green-hover text-white font-medium rounded-lg transition-colors"
    >
      {doneLabel}
    </button>
  </div>
);

/**
 * Reusable filter selection modal
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Close handler
 * @param {string} props.title - Modal title
 * @param {Array} props.options - Array of option strings
 * @param {Array|string} props.selectedValues - Selected value(s)
 * @param {Function} props.onSelect - Selection handler (receives selected value)
 * @param {boolean} props.multiSelect - Whether multiple selections are allowed
 * @param {Function} props.onSelectAll - Select all handler (for multi-select)
 * @param {Function} props.onClearAll - Clear all handler (for multi-select)
 * @param {Function} props.displayFormatter - Optional function to format option display (receives option, returns string)
 * @param {Array<{id: string, label: string, options: string[]}>} [props.groups] - Optional expandable
 *   parent rows (tri-state checkbox + chevron), sorted A-Z together with the flat `options`.
 *   Children use the same selectedValues / onSelect. Dropdown (anchored) mode only; the centered
 *   fallback lists group children flat.
 * @param {Function} [props.onToggleGroup] - Called with the group when its parent checkbox is clicked
 * @param {boolean} [props.searchable] - Force the search box on/off. Default: shown when the list
 *   has more than FILTER_MODAL_SEARCH_MIN_OPTIONS options. Matching is a case-insensitive "contains".
 * @param {string} [props.searchPlaceholder] - Placeholder for the search box
 */
const FilterModal = ({
  isOpen,
  onClose,
  title,
  options = [],
  selectedValues,
  onSelect,
  multiSelect = false,
  onSelectAll,
  onClearAll,
  displayFormatter,
  searchable,
  searchPlaceholder,
  groups = [],
  onToggleGroup,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const listRef = useRef(null);
  const pendingScrollRef = useRef(false);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const toggleGroupExpanded = (id) =>
    setExpandedGroups((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));

  // Grouped lists open on the selection: expand every group that has a selected
  // child, then scroll the first selected row into view.
  useEffect(() => {
    if (!isOpen || groups.length === 0) return;
    const selected = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
    setExpandedGroups(
      groups.filter((g) => g.options.some((o) => selected.includes(o))).map((g) => g.id)
    );
    pendingScrollRef.current = true;
    // Only on open: later selection changes must not re-expand or jump the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Start every open with a clean search; focus the box on pointer devices only
  // (focusing on touch screens would pop the keyboard over the list).
  useEffect(() => {
    setSearchQuery('');
  }, [isOpen]);

  // Anchored mode: open as a dropdown under the trigger button (same look as the
  // commodity selector). Falls back to the centered modal when there is no trigger.
  const [anchorEl, setAnchorEl] = useState(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    setAnchorEl(isOpen ? takeFilterAnchor() : null);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !anchorEl) {
      setPos(null);
      return undefined;
    }
    const update = () => {
      const rect = anchorEl.getBoundingClientRect();
      const margin = 12;
      const width = Math.min(Math.max(rect.width, 248), window.innerWidth - margin * 2);
      const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const openUp = spaceBelow < 260 && spaceAbove > spaceBelow;
      setPos({
        left,
        width,
        top: openUp ? undefined : rect.bottom + 4,
        bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        maxHeight: Math.max(200, Math.min(openUp ? spaceAbove : spaceBelow, 420)),
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen, anchorEl]);

  // Focus the search box on pointer devices only (focusing on touch screens would
  // pop the keyboard over the list). Re-runs once the anchored panel has mounted.
  const panelReady = isOpen && (!anchorEl || !!pos);
  useEffect(() => {
    if (!panelReady) return;
    const isTouch =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(pointer: coarse)').matches;
    if (!isTouch) searchInputRef.current?.focus();
  }, [panelReady]);

  useEffect(() => {
    if (!panelReady || !pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    listRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'center' });
  }, [panelReady, expandedGroups]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const anchored = !!anchorEl;
  if (anchored && !pos) return null;

  const groupChildCount = groups.reduce((n, g) => n + g.options.length, 0);
  const showSearch = shouldShowFilterSearch(searchable, options.length + groupChildCount);
  const isSearching = showSearch && searchQuery.trim() !== '';
  // Select all / Clear all act on every option, so hide them while the list is filtered.
  const showMultiToolbar = multiSelect && onSelectAll && onClearAll && !isSearching;

  const isSelected = (option) => {
    if (multiSelect) {
      return Array.isArray(selectedValues) && selectedValues.includes(option);
    }
    return selectedValues === option;
  };

  const handleOptionClick = (option) => {
    onSelect(option);
    // Single-select: picking a value is the whole interaction, so dismiss.
    // Multi-select stays open until the user clicks outside / Escape / Done.
    if (!multiSelect) onClose();
  };

  const getDisplayValue = (option) => {
    if (displayFormatter) {
      return displayFormatter(option);
    }
    return option === 'N/A' ? 'Not Classified' : option;
  };

  const byLabel = (a, b) => String(getDisplayValue(a)).localeCompare(String(getDisplayValue(b)));

  // Flat list = plain options, plus (when searching, or in the centered fallback)
  // the group children. In grouped dropdowns the full list is sorted A-Z.
  const groupedMode = groups.length > 0 && !isSearching;
  const flatSource =
    groups.length === 0
      ? options
      : [...options, ...groups.flatMap((g) => g.options)].sort(byLabel);
  const visibleOptions = isSearching
    ? filterOptionsBySearch(flatSource, searchQuery, getDisplayValue)
    : (anchored && groupedMode ? options : flatSource);

  // Dropdown entries in one alphabetical sequence: group rows sit among the plain options.
  const entries = (anchored && groupedMode
    ? [
        ...options.map((option) => ({ type: 'option', option, label: String(getDisplayValue(option)) })),
        ...groups.map((group) => ({ type: 'group', group, label: String(group.label) })),
      ].sort((a, b) => a.label.localeCompare(b.label))
    : []);

  const searchBox = showSearch && (
    <div className={anchored ? 'pb-[6px]' : 'px-5 pt-3 pb-1'}>
      <div className="relative">
        <Search
          size={14}
          className="absolute left-[10px] top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
        />
        <input
          ref={searchInputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={searchPlaceholder ?? t('filters.searchPlaceholder', 'Search...')}
          className="w-full bg-[#F5F6F5] dark:bg-[#121513] border border-[#E2E2E2] dark:border-[#262B27] rounded-[10px] px-3 py-2 pl-8 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-[#16A34A] dark:focus:border-[#16A34A]"
        />
      </div>
    </div>
  );

  const toolbar = showMultiToolbar && (
    <FilterModalMultiSelectToolbar
      onSelectAll={onSelectAll}
      onClearAll={onClearAll}
      isDark={isDark}
      selectAllLabel={anchored ? t('filters.selectAllShort', 'Select all') : t('filters.selectAll')}
      clearAllLabel={anchored ? t('filters.clear', 'Clear') : t('filters.clearAll')}
      compact={anchored}
    />
  );

  const emptyText = 'text-[#828382] dark:text-gray-400 text-center py-4';

  // Anchored dropdown rows: same look as the commodity selector items.
  const renderAnchoredOption = (option) => {
    const selected = isSelected(option);
    return (
      <button
        type="button"
        key={option}
        data-selected={selected}
        onClick={() => handleOptionClick(option)}
        className={`w-full flex items-center gap-2 text-left rounded-[8px] px-2.5 py-1.5 transition-colors ${
          selected && !multiSelect
            ? 'bg-[#E7F5EC] dark:bg-[#17311F] text-[#16A34A] font-medium'
            : selected
              ? 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#121513]'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#121513] hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        {multiSelect && (
          <span
            className={`w-[14px] h-[14px] rounded-[4px] border flex items-center justify-center flex-shrink-0 transition-colors ${
              selected ? 'border-[#16A34A] bg-[#16A34A]' : 'border-gray-300 bg-white dark:border-[#3a3c3b] dark:bg-[#121513]'
            }`}
          >
            {selected && <Check size={10} strokeWidth={3} className="text-white" />}
          </span>
        )}
        <span className="block truncate text-[13px]">{getDisplayValue(option)}</span>
      </button>
    );
  };

  const renderGroup = (group) => {
    const selectedCount = group.options.filter((o) => isSelected(o)).length;
    const allSelected = group.options.length > 0 && selectedCount === group.options.length;
    const partial = selectedCount > 0 && !allSelected;
    const expanded = expandedGroups.includes(group.id);
    return (
      <div key={`group-${group.id}`} className="flex flex-col gap-[2px]">
        <div className="flex items-center rounded-[8px] hover:bg-gray-100 dark:hover:bg-[#121513] transition-colors">
          <button
            type="button"
            onClick={() => onToggleGroup?.(group)}
            aria-pressed={allSelected}
            className="flex-1 min-w-0 flex items-center gap-2 text-left px-2.5 py-1.5 text-gray-900 dark:text-white"
          >
            <span
              className={`w-[14px] h-[14px] rounded-[4px] border flex items-center justify-center flex-shrink-0 transition-colors ${
                allSelected || partial
                  ? 'border-[#16A34A] bg-[#16A34A]'
                  : 'border-gray-300 bg-white dark:border-[#3a3c3b] dark:bg-[#121513]'
              }`}
            >
              {allSelected && <Check size={10} strokeWidth={3} className="text-white" />}
              {partial && <Minus size={10} strokeWidth={3} className="text-white" />}
            </span>
            <span className="block truncate text-[13px] font-medium">
              {group.label}
              <span className="ml-1.5 font-normal text-gray-400 dark:text-gray-500">
                {selectedCount}/{group.options.length}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => toggleGroupExpanded(group.id)}
            aria-expanded={expanded}
            aria-label={group.label}
            className="px-2 py-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
        {expanded && (
          <div className="flex flex-col gap-[2px] pl-5">
            {[...group.options].sort(byLabel).map(renderAnchoredOption)}
          </div>
        )}
      </div>
    );
  };

  if (anchored) {
    // Size the panel to the longest label so names are not cut off. The anchor
    // width is only a minimum; the viewport is the upper bound (truncate then
    // applies as a last resort).
    const viewportMax = window.innerWidth - 24;
    const fitWidth = measureLongestLabel([
      ...options.map((o) => ({ text: String(getDisplayValue(o)), extra: multiSelect ? 22 : 0 })),
      ...groups.map((g) => ({ text: String(g.label), bold: true, extra: 22 + 40 + 30 })),
      ...groups.flatMap((g) =>
        g.options.map((o) => ({ text: String(getDisplayValue(o)), extra: 20 + (multiSelect ? 22 : 0) }))
      ),
    ]);
    const panelWidth = Math.min(Math.max(pos.width, fitWidth), viewportMax);
    const panelLeft = Math.max(12, Math.min(pos.left, window.innerWidth - panelWidth - 12));
    return createPortal(
      <div className="fixed inset-0 z-[2000]" onMouseDown={onClose}>
        <div
          onMouseDown={(e) => e.stopPropagation()}
          className="fixed flex flex-col bg-white dark:bg-[#0B0F0C] border border-gray-200 dark:border-[#262B27] rounded-[12px] shadow-xl overflow-hidden p-[6px]"
          style={{
            left: panelLeft,
            width: panelWidth,
            top: pos.top,
            bottom: pos.bottom,
            maxHeight: pos.maxHeight,
          }}
        >
          {toolbar}
          {searchBox}
          <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto">
            {options.length === 0 && groups.length === 0 ? (
              <p className={emptyText}>No options available</p>
            ) : groupedMode ? (
              <div className="flex flex-col gap-[2px]">
                {entries.map((e) => (e.type === 'group' ? renderGroup(e.group) : renderAnchoredOption(e.option)))}
              </div>
            ) : visibleOptions.length === 0 ? (
              <p className={emptyText}>{t('common.noResults', 'No results found')}</p>
            ) : (
              <div className="flex flex-col gap-[2px]">{visibleOptions.map(renderAnchoredOption)}</div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-[#1a1a1a] border border-[#E8EBE9] dark:border-[#3a3c3b] rounded-xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8EBE9] dark:border-[#3a3c3b]">
          <h3 className="text-[#282A29] dark:text-white text-lg font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-[#828382] dark:text-gray-400 hover:text-[#282A29] dark:hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {searchBox}

        {toolbar}

        {/* Options List */}
        <div className="flex-1 overflow-y-auto px-5 py-2">
          {flatSource.length === 0 ? (
            <p className="text-[#828382] dark:text-gray-400 text-center py-4">No options available</p>
          ) : visibleOptions.length === 0 ? (
            <p className="text-[#828382] dark:text-gray-400 text-center py-4">
              {t('common.noResults', 'No results found')}
            </p>
          ) : (
            visibleOptions.map((option) => (
              <button
                type="button"
                key={option}
                onClick={() => handleOptionClick(option)}
                className={`w-full flex items-center gap-3 py-3 border-b border-[#E8EBE9] dark:border-[#3a3c3b] last:border-b-0 ${isDark ? 'hover:bg-white/5' : 'hover:bg-[#F0F5F2]'} transition-colors`}
              >
                <div
                  className={`
                    w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                    ${isSelected(option)
                      ? 'border-bloom-green bg-bloom-green'
                      : isDark
                        ? 'border-green-500'
                        : 'border-bloom-green'
                    }
                  `}
                >
                  {isSelected(option) && (
                    <Check size={14} className="text-white" />
                  )}
                </div>
                <span className="text-[#282A29] dark:text-white text-sm">
                  {getDisplayValue(option)}
                </span>
              </button>
            ))
          )}
        </div>

        <FilterModalDoneFooter onClose={onClose} doneLabel={t('filters.done')} />
      </div>
    </div>
  );
};

export default FilterModal;
